/**
 * Framework seam for the shared components. They import `Link`, `Image` and
 * `usePathname` from `@rai/ui/adapters/navigation`, and each app points that
 * path at its own implementation: `apps/web/adapters/navigation.tsx`
 * (next/link, next/image, next/navigation) and
 * `apps/portal/src/lib/navigation-adapter.tsx` (React Router). This file is the
 * contract and a plain-HTML fallback; no app ships it.
 */
import { forwardRef, useSyncExternalStore, type AnchorHTMLAttributes, type ImgHTMLAttributes } from "react";

export type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { replace: _replace, scroll: _scroll, prefetch: _prefetch, ...props },
  ref,
) {
  return <a ref={ref} {...props} />;
});

export type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "width" | "height"> & {
  src: string;
  width: number;
  height: number;
  priority?: boolean;
};

export function Image({ priority, loading, ...props }: ImageProps) {
  return <img loading={loading ?? (priority ? "eager" : "lazy")} fetchPriority={priority ? "high" : undefined} decoding="async" {...props} />;
}

const subscribe = (onChange: () => void) => {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
};

export function usePathname(): string {
  return useSyncExternalStore(subscribe, () => window.location.pathname, () => "/");
}
